/**
 * INSTALLATION INSTRUCTIONS:
 *
 * 1. Add this file to your theme's assets folder
 * 2. Add to theme.liquid before closing </body> tag:
 *     <script type="module" src="{{ 'locator.js' | asset_url }}"></script>
 * 3. You may control:
 *    - the location detection frequency by changing the INVALIDATE_LOCATION_AFTER_HOURS constant.
 *    - the storage key by changing the LOCATION_STORAGE_KEY constant.
 * 4. An event "userLocationDetected" is dispatched when the location is detected.
 */

(() => {
  const LOCATION_STORAGE_KEY = 'userCountryCode';
  const LOCATION_STORAGE_TIMESTAMP_KEY = 'userCountryCodeTimestamp';
  const INVALIDATE_LOCATION_AFTER_HOURS = 0;

  class LocationDetector {
    constructor() {
      console.log('[LocationDetector] 🚀 Starting location detection system...');
      this.init();
    }

    async init() {
      console.log('[LocationDetector] 🔄 Initializing location detection...');
      
      if (!this.isStorageRestricted()) {
        console.log('[LocationDetector] ✅ Storage is available, checking for stored location...');
        const storedInfo = this.getStoredLocation();
        const storedLocation = storedInfo?.location;
        const storedTimestamp = storedInfo?.timestamp;

        console.log('[LocationDetector] 📦 Stored location info:', { storedLocation, storedTimestamp });

        if (storedLocation && this.isLocationValid(storedTimestamp)) {
          console.log('[LocationDetector] ✅ Using valid stored location:', storedLocation);
          this.dispatchLocationEvent(storedLocation);
          return;
        } else {
          console.log('[LocationDetector] ❌ Stored location invalid or expired, will detect fresh');
        }
      } else {
        console.log('[LocationDetector] ⚠️ Storage restricted, will detect location without caching');
      }

      // If storage is restricted or stored location is invalid/missing, detect new location
      console.log('[LocationDetector] 🌍 Detecting location via API...');
      const locationData = await this.detectAndStoreLocation();

      if (locationData) {
        console.log('[LocationDetector] ✅ Successfully detected location using API:', locationData.country_code);
        return;
      }

      // Fallback to default if everything fails
      console.log('[LocationDetector] ❌ Detection failed, using DEFAULT fallback');
      this.dispatchLocationEvent('DEFAULT');
    }

    isStorageRestricted() {
      try {
        if (typeof localStorage === 'undefined') {
          console.log('[LocationDetector] localStorage is not defined');
          return true;
        }

        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
        return false;
      } catch (e) {
        console.log('[LocationDetector] Storage access is restricted');
        return true;
      }
    }

    isLocationValid(storedTimestamp) {
      if (!storedTimestamp) return true;

      // Check if stored location is less than (INVALIDATE_LOCATION_AFTER_HOURS) hours old
      const invalidateLocationAfterHours = INVALIDATE_LOCATION_AFTER_HOURS * 60 * 60 * 1000;
      const timestamp = new Date(storedTimestamp).getTime();
      const isValid = (Date.now() - timestamp) < invalidateLocationAfterHours;

      return isValid;
    }

    getStoredLocation() {
      if (this.isStorageRestricted()) {
        return null;
      }

      try {
        const storedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
        const storedTimestamp = localStorage.getItem(LOCATION_STORAGE_TIMESTAMP_KEY);

        return {
          location: storedLocation ? JSON.parse(storedLocation) : null,
          timestamp: storedTimestamp ? JSON.parse(storedTimestamp) : null
        };
      } catch (error) {
        console.log('[LocationDetector] Unable to access stored location');
        return null;
      }
    }

    async detectAndStoreLocation() {
      try {
        console.log('[LocationDetector] 🌐 Fetching location from /browsing_context_suggestions.json...');
        const response = await fetch('/browsing_context_suggestions.json');
        
        console.log('[LocationDetector] 📡 API Response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('[LocationDetector] 📄 Raw API response:', data);

        if (!data?.detected_values?.country) {
          console.log('[LocationDetector] ❌ No country data in response structure');
          throw new Error('No country data in API response');
        }

        const locationData = {
          country_code: data.detected_values.country.handle,
          country_name: data.detected_values.country.name,
          timestamp: new Date().toISOString()
        };

        console.log('[LocationDetector] ✅ Parsed location data:', locationData);

        if (!this.isStorageRestricted()) {
          try {
            localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(locationData.country_code));
            localStorage.setItem(LOCATION_STORAGE_TIMESTAMP_KEY, JSON.stringify(locationData.timestamp));
            console.log('[LocationDetector] 💾 Location stored in localStorage');
          } catch (storageError) {
            console.log('[LocationDetector] ⚠️ Unable to store location:', storageError);
          }
        }

        this.dispatchLocationEvent(locationData.country_code);
        return locationData;
      } catch (error) {
        console.log('[LocationDetector] ❌ Location detection failed:', error.message);
        this.dispatchLocationEvent('DEFAULT');
        return null;
      }
    }

    dispatchLocationEvent(location) {
      console.log('[LocationDetector] 📢 Dispatching userLocationDetected event with:', location);
      const event = new CustomEvent('userLocationDetected', {
        detail: location
      });
      window.dispatchEvent(event);
      console.log('[LocationDetector] ✅ Event dispatched successfully');
    }
  }

  // Initialize on DOM load
  document.addEventListener('DOMContentLoaded', () => {
    console.log('[LocationSystem] 🏁 DOM ready, starting location detector...');
    
    // Check if we're on a page with cart elements
    const hasCartDrawer = document.querySelector('cart-drawer');
    const hasCartPage = document.querySelector('#main-cart-footer');
    console.log('[LocationSystem] 🛒 Cart elements present:', { drawer: !!hasCartDrawer, page: !!hasCartPage });
    
    new LocationDetector();
  });
})();